import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const credentials = [
  { icon: 'ri-brain-line', title: 'Clinical Psychologist', detail: 'Advanced training in trauma-informed care' },
  { icon: 'ri-heart-pulse-line', title: 'Licensed Therapist', detail: '15+ years in clinical practice' },
  { icon: 'ri-award-line', title: 'Certified Transformation Coach', detail: 'Master-level coaching certification' },
  { icon: 'ri-body-scan-line', title: 'Somatic Healing Practitioner', detail: 'Trained in nervous system regulation' },
  { icon: 'ri-landscape-line', title: 'Retreat Facilitator', detail: 'International retreat leadership' },
  { icon: 'ri-calendar-event-line', title: 'Conference Host', detail: 'Women Who Heal annual conference' },
];

export default function CredentialsTrainingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
      if (gridRef.current) {
        const cards = gridRef.current.children;
        gsap.fromTo(
          cards,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.1,
            scrollTrigger: {
              trigger: gridRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-14">
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            Training &amp; Credentials
          </p>
          <h2 className="font-heading text-xl md:text-3xl lg:text-5xl leading-[1.2] text-[#1A1A1A]">
            The clinical depth
            <br />
            behind the work.
          </h2>
        </div>

        {/* Gold Divider */}
        <div className="w-16 h-px bg-[#C9A84C] mx-auto mb-14" />

        {/* Credentials Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {credentials.map((cred) => (
            <div
              key={cred.title}
              className="bg-white rounded-lg p-6 md:p-8 flex flex-col items-center text-center border border-[#C9A84C]/15 hover:border-[#C9A84C]/40 transition-colors duration-300"
            >
              <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 flex items-center justify-center mb-5">
                <i className={`${cred.icon} text-xl text-[#C9A84C]`} />
              </div>
              <h3 className="font-heading text-base md:text-lg text-[#1A1A1A] mb-2 font-semibold">
                {cred.title}
              </h3>
              <p className="font-body text-xs md:text-sm text-[#5A5A5A] leading-relaxed">
                {cred.detail}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}